import { DialogConfig } from '@agent-ds/shared/components/dialog/dialog-config';
import { DialogRef } from '@agent-ds/shared/components/dialog/dialog-ref';
import { JobAzCheckCompanyConfig } from '@agent-ds/shared/components/mail-send/configs/job-az-check-company-config';
import { MailSendConfig } from '@agent-ds/shared/components/mail-send/config';
import { MailSendFlowDialogComponent } from '@agent-ds/shared/components/mail-send-flow-dialog/mail-send-flow-dialog.component';
import { PopupControlComponent } from '@agent-ds/shared/components/popup-control/popup-control.component';
import { environment } from '@agent-ds/environments/environment';
import { Job } from '@agent-ds/shared/interfaces';
import { CompanyApiService, JobApiService } from '@agent-ds/shared/services';
import { deepClone, getValueFromObject } from '@agent-ds/shared/util/util';
import { Component, ElementRef, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { JobCategory } from '@agent-ds/shared/interfaces/job-az-check';
import { JobAzCheckService } from '../services/job-az-check.service';
import { SalaryTableRowsService } from '@agent-ds/jobs/services/salary-table-rows.service';
import { JobCompanyConfig } from '@agent-ds/shared/components/mail-send/configs/job-company-config';

export enum AzCheckStep {
  SelectCategory = 'SelectCategory', // 種別選択
  CheckResult = 'CheckResult', // チェック結果確認
  SendToCompany = 'SendToCompany', // 企業送信
  Consultation = 'Consultation', // 企画相談
}

export const SUB_TITLE: { [key in AzCheckStep]: string } = {
  [AzCheckStep.SelectCategory]: '',
  [AzCheckStep.CheckResult]: 'チェック内容確認',
  [AzCheckStep.SendToCompany]: '企業送信',
  [AzCheckStep.Consultation]: '企画相談',
};

@Component({
  selector: 'ag-job-az-check-dialog-on-create',
  templateUrl: './job-az-check-dialog-on-create.component.html',
  styleUrls: ['./job-az-check-dialog-on-create.component.scss'],
  providers: [JobAzCheckService, SalaryTableRowsService],
})
export class JobAzCheckDialogOnCreateComponent implements OnInit {
  readonly AzCheckStep = AzCheckStep;

  step: AzCheckStep = AzCheckStep.SelectCategory;
  isLoading: boolean = false;

  url: string = '';
  azCheckCompanyConfig = new JobAzCheckCompanyConfig();
  companyConfig = new JobCompanyConfig();

  private selectedJobCategory: JobCategory;

  get validity(): boolean {
    return this.jobAzCheckService.validity;
  }

  get canSendToCompany(): boolean {
    return this.jobAzCheckService.canSendToCompany;
  }

  get hasUnsavedChanges(): boolean {
    return this.jobAzCheckService.hasUnsavedChanges;
  }

  constructor(
    public dialog: DialogRef,
    public config: DialogConfig<{ job: Job }>,
    private jobApiService: JobApiService,
    private companyApiService: CompanyApiService,
    private elementRef: ElementRef,
    public jobAzCheckService: JobAzCheckService,
  ) {
    if (!config.data || !config.data.job) {
      this.dialog.close();
      return;
    }
  }

  ngOnInit(): void {
    this.jobAzCheckService.init(this.config.data!.job);
  }

  getSubTitle(): string {
    return SUB_TITLE[this.step];
  }

  close(): void {
    this.dialog.close();
  }

  startCheck(category: JobCategory): void {
    if (!category) {
      return;
    }
    this.selectedJobCategory = category;
    this.isLoading = true;
    this.jobAzCheckService.requestAzCheck(category).subscribe(
      (result) => {
        this.jobApiService.refreshEvent.emit();
        this.jobAzCheckService.setCheckResult(result);
        this.step = AzCheckStep.CheckResult;
        this.isLoading = false;
        this.scrollToTop();
      },
      () => {
        this.close();
        this.isLoading = false;
      },
    );
  }

  sendToCompany(): void {
    if (this.hasUnsavedChanges) {
      return;
    }

    PopupControlComponent.instance.open({
      title: '求人票共有形式の選択',
      content: '求人票を企業へ共有する形式を選択してください。',
      cancelText: 'キャンセル',
      confirmText: 'URLで共有',
      confirmCallback: () => {
        this.step = AzCheckStep.SendToCompany;
        setTimeout(() => {
          this.startMailFlow(this.azCheckCompanyConfig);
        });
      },
      multiConfirm: [{
        confirmText: 'Excelで共有',
        confirmCallback: () => {
          this.step = AzCheckStep.SendToCompany;
          setTimeout(() => {
            this.startMailFlow(this.companyConfig);
          });
        },
      }],
    });
  }

  startMailFlow(config: MailSendConfig): void {
    if (!MailSendFlowDialogComponent.instance) {
      return;
    }

    const job = this.jobAzCheckService.job;
    MailSendFlowDialogComponent.instance.config = config;
    const params: { jobs: any[]; department: any; enterprise: any; postSendMailCallback?: Function; urlAccessToken?: string } = {
      jobs: [
        {
          id: job.id,
          frontId: job.frontId,
          position: getValueFromObject(job.dynamicData, 'position'),
          enterpriseName: job.enterpriseName,
          enterpriseId: job.enterpriseId,
          frontEnterpriseId: job.frontEnterpriseId,
          enterpriseDepartmentId: job.enterpriseDepartmentId,
          jobUsers: job.enterpriseDepartmentUsers,
        },
      ],
      department: null,
      enterprise: this.jobAzCheckService.companyData,
    };

    if (config instanceof JobAzCheckCompanyConfig) {
      forkJoin([
        this.companyApiService.getDepartments(job.enterpriseId),
        this.jobApiService.issueCompanyCredentials(job.id),
      ]).subscribe({
        next: ([departments, companyCredentials]) => {
          params.department = departments.enterpriseDepartments.find((d) => d.id === job.enterpriseDepartmentId);
          params.urlAccessToken = companyCredentials.urlAccessToken;
          params.postSendMailCallback = () => this.jobApiService.saveJobCompanySharedFormat(this.jobAzCheckService.job.id, 'url').subscribe();
          config.setParams(params);
          MailSendFlowDialogComponent.instance!.start({
            additionalReplacements: [{
              label: '企業側求人票確認用URL',
              value: `${environment.myPageUrl}/job-company-az-check/${companyCredentials.urlAccessToken}`,
              withoutAppendingNewline: true,
            }]
          });
        },
        error: (error) => {
          this.step = AzCheckStep.CheckResult;
        }
      });
    } else if (config instanceof JobCompanyConfig) {
      this.companyApiService.getDepartments(job.enterpriseId).subscribe(
        (departments) => {
          params.department = departments.enterpriseDepartments.find((d) => d.id === job.enterpriseDepartmentId);
          params.postSendMailCallback = () => this.jobApiService.saveJobCompanySharedFormat(this.jobAzCheckService.job.id, 'excel').subscribe();
          config.setParams(params);
          MailSendFlowDialogComponent.instance!.start();
        },
        (error) => {
          this.step = AzCheckStep.CheckResult;
        }
      );
    } else {
      config.setParams(params);
      MailSendFlowDialogComponent.instance.start();
    }
  }

  consult(): void {
    if (this.hasUnsavedChanges) {
      return;
    }

    this.step = AzCheckStep.Consultation;
  }

  tempSave(): void {
    const companyData = this.jobAzCheckService.companyData;
    if (!companyData) {
      return;
    }

    const job = this.jobAzCheckService.job;
    const jobDynamicData: any = deepClone(job.dynamicData);
    delete jobDynamicData.raId;
    delete jobDynamicData.paId;
    delete jobDynamicData.asId;
    const enterpriseDynamicData: any = deepClone(companyData.dynamicData);
    const body = {
      jobCategory: this.selectedJobCategory,
      jobDynamicData: jobDynamicData,
      enterpriseDynamicData: enterpriseDynamicData,
    };

    this.jobApiService.tempSaveAzCheck(job.id, body).subscribe({
      next: (result) => {
        this.jobApiService.refreshEvent.emit();
        this.jobAzCheckService.hasUnsavedChanges = false;
        this.jobAzCheckService.setCheckResult(result);
        this.step = AzCheckStep.CheckResult;
        this.scrollToTop();
      },
      error: (error) => {
        if (error.status === 401) {
          this.close();
          return;
        }
      },
    });
  }

  private scrollToTop(): void {
    const body = this.elementRef.nativeElement.querySelector('.dialog__body--check-result');
    if (body) {
      body.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
